import styled, {css, keyframes} from "styled-components";
import {fontFamilies, fontWeights, motion, radii, showcase, topbar, zIndex} from "theme/tokens";

export {DisplayAccent as TitleAccent} from "theme/typography";

/**
 * Externalized styles for {@link ./CardSheet} (ui#1054), per the owner's no-inline-styles rule
 * (CLAUDE.md, 2026-07-12).
 *
 * A FLOATING CARD, not an edge-attached aside: it sits in the band below the top pill bar, detached
 * from the right and bottom viewport edges, with card corners on all four sides and the SAME elevation
 * as the bar itself (`theme.tokens.shadowCard`, the token `NavBar.styles`'s pill uses). Reading as one
 * family with the bar is the whole point of ui#1054, so the shadow is taken from the token rather than
 * re-specified here — a second hand-written shadow would drift away from the bar on the first change.
 *
 * It is a plain `position: fixed` element — deliberately NOT a MUI `Dialog`/`Modal`. No portal, no
 * scrim, no focus trap, and `zIndex.panel` sits below `zIndex.nav`, so the bar stays visible AND
 * clickable above it and never leaves the accessibility tree. Every colour comes from `theme.tokens`
 * (light + dark for free); geometry and timing come from the token constants.
 */

/**
 * The gap between the card and the viewport edges — and between the card and the bar above it.
 *
 * A local constant rather than a token because it is this surface's own inset, and it is used in four
 * places that must agree: the three offsets, the width clamp, and the closed transform (which must
 * carry the card past the gap as well as its own width, or a sliver stays on screen).
 *
 * DESKTOP ONLY. There is deliberately no mobile counterpart: below 640px the sheet goes full-bleed,
 * because the gutters an inset would cost are content width a phone does not have (see the media
 * query on {@link Card}).
 */
const INSET = "14px";

/**
 * Every card sheet's width, and the one a caller gets without asking.
 *
 * Matching {@link ../SideSheet}'s `wide` size, which `e2e/sheetGeometry.ts`'s "right half" rule caps at
 * 640px. Account, Billing, Analytics and Transfers all ride on it: the Account sheet's cards span its
 * content width (kitecorp/transferify-ui#3677) and the Billing sheet caps its column at
 * {@link SHEET_READING_WIDTH} inside it (ui#1816) — so this number is a shared contract rather than a
 * default anyone may raise.
 */
const WIDTH = "600px";

/**
 * The `wide` size, for a sheet whose body is genuinely TWO columns (ui#1942).
 *
 * Opted into per caller, never inherited: the builder puts a live miniature of the recipient's page
 * beside its controls, and at {@link WIDTH} that miniature's art was squeezed to about 30% of a 252px
 * column. Every other sheet is one column of reading matter and gets nothing from the extra pixels.
 *
 * Applied only from `showcase.minWidthPx` up, the breakpoint the recipient page already treats as "this
 * screen has room for two things side by side" — shared rather than re-picked so the app has one answer
 * to that question. Below it the wide sheet is byte-identical to every other one.
 *
 * The card stays RIGHT-ANCHORED at both sizes: it grows leftward from the same `INSET`. Whether a wide
 * sheet should instead centre is an open owner question on ui#1942 — moving the anchor changes what
 * kind of surface this is, so it is not an implementer's call.
 */
const WIDTH_WIDE = "1100px";

/**
 * The floating card. Desktop: {@link WIDTH} wide (or {@link WIDTH_WIDE} for a caller that asks), hung
 * `INSET` below the bar and inset from the right and bottom edges. Enter = slide from the right over
 * `motion.enter` (320ms) easeOut; exit = the quicker `motion.exit` (180ms) easeIn;
 * `prefers-reduced-motion` collapses both to a 120ms opacity fade with no transform. Driven by a single
 * `data-state` attribute so the whole animation lives in CSS (see {@link ./CardSheet} for the small
 * state machine that sets it).
 */
export const Card = styled.section<{$wide: boolean}>`
	position: fixed;
	/* Hung below the top CHROME rather than below the bar (ui#1188): the two are the same height until
	   a banner is showing, and a fixed card cannot see the in-flow banner region on its own. */
	top: calc(${topbar.clearance} + ${INSET});
	right: ${INSET};
	bottom: ${INSET};
	z-index: ${zIndex.panel};
	width: min(${WIDTH}, calc(100vw - (2 * ${INSET})));
	max-width: 100%;
	display: flex;
	flex-direction: column;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.card};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	/* Start clear of the edge — its own width PLUS the gap — so no sliver shows before it opens. */
	transform: translateX(calc(100% + ${INSET}));
	transition: transform ${motion.enter} ${motion.easeOut};
	outline: none;

	&[data-state="open"] {
		transform: translateX(0);
	}

	&[data-state="closing"] {
		transform: translateX(calc(100% + ${INSET}));
		transition: transform ${motion.exit} ${motion.easeIn};
	}

	/* The opt-in second size — see {@link WIDTH_WIDE}. Nothing but the width changes: the anchor, the
	   inset, the corner and the slide are the family's, and a wide sheet has to stay recognisably one of
	   them. The closed transform is a PERCENTAGE of the card's own width, so it still carries the wider
	   card fully off screen without a second number here. */
	${({$wide}) => $wide && css`
		@media (min-width: ${showcase.minWidthPx}px) {
			width: min(${WIDTH_WIDE}, calc(100vw - (2 * ${INSET})));
		}
	`}

	/*
	 * FULL-BLEED below 640px, and that is a width decision rather than a taste one. A phone has no
	 * pixels to spend on decoration: insetting the card costs two mobile gutters plus the body's own
	 * padding, and the surfaces inside — the reminder detail's file list above all — need every one of
	 * them. Insetting it took ~17px off the content box and pushed that list into horizontal overflow
	 * at 375px, which e2e/selected-zip-owner.spec.ts caught. So the floating card is a DESKTOP
	 * treatment (ui#1054) and the phone keeps the edge-to-edge sheet, hung under the shorter mobile
	 * bar, with the top corners still rounded so it stays the same family.
	 */
	@media (max-width: 640px) {
		top: ${topbar.clearanceMobile};
		right: 0;
		left: 0;
		bottom: 0;
		width: auto;
		/* Only the top hairline survives: the other three edges meet the viewport, and each one costs a
		   pixel of content width that the file list below does not have to spare. */
		border-left: none;
		border-right: none;
		border-bottom: none;
		border-radius: ${radii.card} ${radii.card} 0 0;
		transform: translateX(100%);

		&[data-state="closing"] {
			transform: translateX(100%);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		transform: none;
		opacity: 0;
		transition: opacity 120ms linear;

		&[data-state="open"] {
			opacity: 1;
		}

		&[data-state="closing"] {
			transform: none;
			opacity: 0;
			transition: opacity 120ms linear;
		}
	}
`;

/** Sticky header: sheet title on the left, a ✕ on the right, hairline underneath. */
export const Head = styled.div<{$hasSubtitle: boolean}>`
	flex: none;
	display: flex;
	align-items: ${({$hasSubtitle}) => ($hasSubtitle ? "flex-start" : "center")};
	justify-content: space-between;
	gap: 12px;
	padding: 18px 18px 14px 24px;
	border-bottom: 1px solid ${({theme}) => theme.tokens.line};

	@media (max-width: 640px) {
		padding: 15px 14px 12px 18px;
	}
`;

/** Title + optional supporting copy, kept together opposite the close button. */
export const TitleCopy = styled.div`
	min-width: 0;
	/* Sheet bodies may inherit centered editorial copy; the shared head owns one visible left edge. */
	text-align: left;
`;

/**
 * The serif sheet title, drawn to the same scale as the shared side sheet's.
 *
 * `$plain` is the per-caller opt-out (kitecorp/transferify-ui#1953, owner-approved prototype): the
 * personalization builder's head sits above a two-column working surface, where a 30px serif line
 * competes with the controls it introduces rather than opening a page of reading matter. It is a
 * variant of THIS component rather than a second title element, so the two can never drift in the
 * things they do share — the margin, the tracking and the ink token.
 */
export const Title = styled.h2<{$plain?: boolean}>`
	margin: 0;
	font-family: ${({$plain}) => ($plain ? "inherit" : fontFamilies.displaySerif)};
	font-weight: ${({$plain}) => ($plain ? 700 : fontWeights.display)};
	font-size: ${({$plain}) => ($plain ? "18px" : "clamp(24px, 4vw, 30px)")};
	line-height: ${({$plain}) => ($plain ? 1.25 : 1.05)};
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The sheet's comfortable reading measure: narrower than the ~552px body so a line of copy, or a
 * column of cards, stops short of the gutters instead of running edge to edge.
 *
 * Named and exported because three surfaces have to agree on it: the head's supporting copy below,
 * the Billing sheet's card column ({@link ../../account/billing/BillingContent.styles}), which the
 * owner asked to narrow to match the rest of the sheet (ui#1816), and the Options sheet's controls
 * ({@link ../../home/components/OptionsSheet.styles}, ui#2060). Three literal `480px`s would drift.
 * The Account sheet's column read it too until the owner found its cards crowded and had them span
 * the body (kitecorp/transferify-ui#3677, {@link ../../account/profile/ProfileContent.styles}).
 */
export const SHEET_READING_WIDTH = "480px";

/** Supporting copy directly under the sheet title. */
export const Subtitle = styled.p`
	margin: 7px 0 0;
	max-width: ${SHEET_READING_WIDTH};
	font-size: 14.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** Design `.x-btn` — kept ≥44px so it stays a comfortable touch target on a phone. */
export const CloseButton = styled.button`
	flex: none;
	width: 44px;
	height: 44px;
	border-radius: 11px;
	border: none;
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;
	font-size: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background ${motion.hover}, color ${motion.hover};

	&:hover {
		background: ${({theme}) => theme.tokens.line};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The scroll region's TOP padding, exported because a `position: sticky` child of the body has to
 * cancel it.
 *
 * A sticky `top: 0` pins against the containing block's CONTENT box, which starts below this padding —
 * so a bar that means "pin to the top of the visible scroll area" lands this far down instead, leaving
 * a window of exactly this height through which the content behind it keeps showing. A child that
 * wants to pin flush offsets by `calc(-1 * BODY_PADDING_TOP)`; sharing the value is what keeps the two
 * in step, because the offset is only correct while it equals this padding.
 */
export const BODY_PADDING_TOP = "20px";

/** {@link BODY_PADDING_TOP} for the full-bleed phone card — same rule, smaller gutter. */
export const BODY_PADDING_TOP_MOBILE = "18px";

/**
 * Dissolves the top of the scroll region instead of guillotining it (ui#1281).
 *
 * A scroll container clips at its padding-box edge, and this one's edge lands a hairline below the
 * head's own divider. So anything crossing it is cut by a straight, full-width horizontal line drawn
 * immediately under another straight, full-width horizontal line — and a half glyph in that position
 * does not read as "there is more above", it reads as a rendering fault. That is the report: "Culoare
 * de accent" sliced mid-letter, and with it every field label, card top and image that passes through.
 *
 * An ALPHA MASK rather than a painted gradient, for two reasons that both matter here. It needs no
 * colour, so it cannot disagree with the card underneath in either mode or on any brand — a gradient
 * fading to `tokens.card` would be one more place for a surface colour to drift. And it fades the
 * content itself rather than covering it, so nothing new is stacked over the scrollport for a
 * `position: sticky` child to fight with.
 *
 * The band is EXACTLY the scroll region's own top padding, and at `scrollTop: 0` nothing IN FLOW
 * reaches into it — so an unscrolled sheet reads at full strength and the dissolve exists only once
 * content is genuinely behind the head, the state that was broken. (An out-of-flow child CAN reach
 * there: `home/components/FormField`'s tip opens upward out of its row and is already clipped by this
 * scroll region at rest, which ui#1343 owns. The claim is about flow content, not about every box.)
 *
 * `scroll-padding-top` is the mask's REQUIRED COMPANION rather than an alternative to it, and the
 * distinction cost a review round. Neither it nor `scroll-margin` does anything for free scrolling —
 * that half is what the mask is for. But they own the OTHER way a control arrives at this edge:
 * scroll-into-view, which is what a Tab press performs. Without this, a focused control the sender had
 * already wheel-scrolled to 6px below the edge counts as "partially visible", so the browser declines
 * to scroll it — and it and its focus ring then sit at ~20-30% alpha, which is a fade turning an
 * indicator OFF. Measured: with the padding the same control parks at the full band instead. The value
 * is shared with the fade for the reason {@link BODY_PADDING_TOP} already gives for the sticky offset —
 * it is only correct while the two agree.
 *
 * Unprefixed, and that is checked rather than assumed for THIS declaration: `npx browserslist` resolves
 * this product's `production` query to `safari 26.5` / `ios_saf 18.5+`, and Safari has served
 * `mask-image` under its standard name since 15.4, so a `-webkit-` twin would guard a browser this
 * build does not target. styled-components would not add one either — its prefixer is gated behind
 * `enableVendorPrefixes`, which this repo never sets.
 */
function scrollEdgeFade(band: string) {
	return css`
		mask-image: linear-gradient(to bottom, transparent 0, #000 ${band});
		scroll-padding-top: ${band};
	`;
}

/**
 * The internal scroll region (content scrolls HERE, not the page), padded so the surfaces inside need
 * no gutters of their own. `overscroll-behavior: contain` stops a scroll at the sheet's edge from
 * chaining to the canvas behind it.
 *
 * The bottom padding is the sheet's landing space, and it only became real when {@link BodyFade} stopped
 * shrinking below its content: until then an overflowing sheet's content spilled out of that wrapper and
 * scrolled straight through this padding, so the personalization sheet's last row ended a measured 0.4px
 * above the card edge however much was declared here. It is deliberately deeper than the top: a scroll
 * that ends flush against the card's border reads as cut off, and the last row is usually the one being
 * acted on (a Save, a destructive confirm).
 */
export const Body = styled.div<{$fadeScrollEdge: boolean}>`
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: contain;
	display: flex;
	flex-direction: column;
	padding: ${BODY_PADDING_TOP} 24px 32px;
	${({$fadeScrollEdge}) => ($fadeScrollEdge && scrollEdgeFade(BODY_PADDING_TOP))}

	/*
	 * 16px side gutters on a phone, restoring EXACTLY what the feature bodies carried before ui#1038
	 * moved the padding off the pages and onto the sheet. That move widened the gutters from 16px to
	 * 20px a side, which cost ~8px of content box and pushed the reminder detail's file list into
	 * horizontal overflow at 375px — a regression that reached master and is what
	 * e2e/selected-zip-owner.spec.ts fails on there. Widen these again and that spec goes red.
	 */
	@media (max-width: 640px) {
		padding: ${BODY_PADDING_TOP_MOBILE} 16px calc(28px + env(safe-area-inset-bottom));
		${({$fadeScrollEdge}) => ($fadeScrollEdge && scrollEdgeFade(BODY_PADDING_TOP_MOBILE))}
	}
`;

/** Sticky footer rail pinned below the scroll region, rounded to meet the card's bottom corners. */
export const Footer = styled.div`
	flex: none;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card};
	border-radius: 0 0 ${radii.card} ${radii.card};
	padding: 14px 24px;

	@media (max-width: 640px) {
		/* Meets the viewport edge rather than a rounded corner once the sheet goes full-bleed. */
		border-radius: 0;
		padding: 12px 18px calc(12px + env(safe-area-inset-bottom));
	}
`;

/** Keyframes for the ~150ms body crossfade replayed when a caller swaps the body under one open sheet. */
const bodyFadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

/**
 * The keyed body wrapper: remounting it (its `key` changes when the caller swaps content, e.g. the
 * feature sheet switching destination) replays a short opacity fade-in, giving the crossfade without
 * touching the card shell or the canvas behind it. It still GROWS to fill a body whose content falls
 * short, so a `height: 100%` body resolves against a definite height as before.
 *
 * What it must never do again is SHRINK. It carried `flex: 1 1 auto` with `min-height: 0`, which let the
 * flex layout size it to the body's content box and leave its own content hanging out of it — measured at
 * 505px of box holding 729px of content on the personalization sheet, and 527px holding 2282px on a long
 * reminders list. A wrapper that lies about its height breaks the two things that are measured from it:
 * the body's `padding-bottom` lands mid-content instead of after the last row (the sheet then scrolls to
 * a flush, cut-off edge), and a `position: sticky` child can only stay pinned while the scroll is inside
 * this box, so a tab strip silently unpins partway down and scrolls away with the content.
 *
 * Not shrinking costs nothing: these bodies already scroll at the scroll region above, and none of them
 * nests a scroller of its own (verified against the reminders list at 25 items — zero inner scrollers
 * before and after), so sizing to content changes what the wrapper REPORTS, not what the sheet does.
 */
export const BodyFade = styled.div`
	flex: 1 0 auto;
	display: flex;
	flex-direction: column;
	animation: ${bodyFadeIn} 150ms ${motion.easeOut} both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;
